import type { AxiosInstance } from 'axios';

export type LifecycleEventSource = 'CUSTOMER' | 'VENDOR' | 'PLATFORM' | 'SYSTEM';
export type CustomerLifecycleStage =
  | 'NEW'
  | 'ACTIVE'
  | 'REPEAT'
  | 'AT_RISK'
  | 'DORMANT'
  | 'CHURNED';

export interface LifecycleEvent {
  id: string;
  source: LifecycleEventSource;
  subjectId: string;
  kind: string;
  refType: string | null;
  refId: string | null;
  payload: unknown | null;
  createdAt: string;
}

export interface CustomerLifecycleMetrics {
  userId: string;
  stage: CustomerLifecycleStage;
  lifetimeValuePaise: number;
  orderCount: number;
  avgOrderValuePaise: number;
  firstOrderAt: string | null;
  lastOrderAt: string | null;
  lastSeenAt: string | null;
  daysSinceLastOrder: number | null;
}

export interface AbandonedCartScanResult {
  scannedCount: number;
  abandonedCount: number;
  nudgesDispatched: number;
}

export interface RecordEventRequest {
  source: LifecycleEventSource;
  subjectId: string;
  kind: string;
  refType?: string;
  refId?: string;
  payload?: Record<string, unknown>;
}

export interface CrmSegment {
  id: string;
  name: string;
  description: string | null;
  filters: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export type CrmCampaignStatus = 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'COMPLETED';

export interface CrmCampaign {
  id: string;
  name: string;
  description: string | null;
  status: CrmCampaignStatus;
  segmentId: string | null;
  journey: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export type CommunicationChannel = 'EMAIL' | 'SMS' | 'WHATSAPP' | 'PUSH';
export type ConsentStatus = 'OPT_IN' | 'OPT_OUT';

export interface CommunicationConsent {
  id: string;
  userId: string;
  channel: CommunicationChannel;
  status: ConsentStatus;
  updatedAt: string;
}


export function crmEndpoints(http: AxiosInstance) {
  return {
    recordEvent: (body: RecordEventRequest) =>
      http.post('/admin/crm/events', body).then((r) => r.data),
    listEvents: (subjectId: string, params?: { kind?: string; limit?: number }) =>
      http
        .get<{ data: LifecycleEvent[] }>(`/admin/crm/events/${subjectId}`, { params })
        .then((r) => r.data.data),
    customerLifecycle: (userId: string) =>
      http
        .get<CustomerLifecycleMetrics>(`/admin/crm/customers/${userId}/lifecycle`)
        .then((r) => r.data),
    runAbandonedCartScan: (body: { thresholdMinutes?: number; maxDispatch?: number }) =>
      http
        .post<AbandonedCartScanResult>('/admin/crm/automations/abandoned-cart-scan', body)
        .then((r) => r.data),

    // Segments
    listSegments: () => http.get<CrmSegment[]>('/admin/crm/segments').then((r) => r.data),
    createSegment: (body: { name: string; description?: string; filters: Record<string, unknown> }) =>
      http.post<CrmSegment>('/admin/crm/segments', body).then((r) => r.data),
    
    // Campaigns
    listCampaigns: () => http.get<CrmCampaign[]>('/admin/crm/campaigns').then((r) => r.data),
    createCampaign: (body: { name: string; description?: string; segmentId?: string; journey?: Record<string, unknown> }) =>
      http.post<CrmCampaign>('/admin/crm/campaigns', body).then((r) => r.data),
    updateCampaign: (id: string, body: Partial<CrmCampaign>) =>
      http.patch<CrmCampaign>(`/admin/crm/campaigns/${id}`, body).then((r) => r.data),
    
    // Consent
    listConsents: (userId: string) => http.get<CommunicationConsent[]>(`/admin/crm/users/${userId}/consent`).then((r) => r.data),
    updateConsent: (userId: string, body: { channel: CommunicationChannel; status: ConsentStatus }) =>
      http.put<CommunicationConsent>(`/admin/crm/users/${userId}/consent`, body).then((r) => r.data),
  };
}

export type CrmEndpoints = ReturnType<typeof crmEndpoints>;
